import React, { useState } from 'react';
import { Download, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { cn } from '../../lib/utils';
import { exportToCsv, type CsvColumn } from '../../lib/csv';
import { toast } from 'sonner';

interface ExportCsvButtonProps<T> {
  /**
   * Async function to fetch the rows to export. Should respect current filters.
   * Return all rows (paginated internally if needed).
   */
  fetchRows: () => Promise<T[]>;
  columns: CsvColumn<T>[];
  filename: string;
  isDark: boolean;
  disabled?: boolean;
}

export function ExportCsvButton<T>({
  fetchRows,
  columns,
  filename,
  isDark,
  disabled,
}: ExportCsvButtonProps<T>) {
  const [busy, setBusy] = useState(false);

  const handle = async () => {
    setBusy(true);
    try {
      const rows = await fetchRows();
      if (rows.length === 0) {
        toast.info('Nothing to export');
        return;
      }
      const ts = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
      exportToCsv(rows, columns, `${filename}_${ts}.csv`);
      toast.success(`Exported ${rows.length} rows`);
    } catch (e: any) {
      toast.error(e?.response?.data?.message || e?.message || 'Export failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Button
      type="button"
      variant="outline"
      onClick={handle}
      disabled={busy || disabled}
      className={cn(
        'rounded-xl gap-2',
        isDark ? 'border-[#2D2D38] hover:bg-[#0F0F17]' : 'border-[#EFEAE0] hover:bg-[#F5F3EF]',
      )}
    >
      {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
      Export CSV
    </Button>
  );
}
